import {
  CodatAuthContextType,
  useCodatAuth,
} from "@codat/client-cookie-manager";
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { Options, UseAxios, UseAxiosResult } from "axios-hooks";

import { ApiError } from "./shared";

export const useApiRequest = <
  TResponse,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TBody = any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TError extends ApiError = any
>(
  useAxios: UseAxios,
  serviceName: string,
  requestConfig: AxiosRequestConfig | string,
  options?: Options
): UseAxiosResult<TResponse, TBody, TError> => {
  const auth = useCodatAuth();
  const token = auth.userData?.access_token;

  const resolvedConfig: AxiosRequestConfig =
    typeof requestConfig === "object"
      ? {
          ...requestConfig,
          headers: {
            ...(requestConfig.headers || {}),
            ...(token ? { authorization: `Bearer ${token}` } : {}),
          },
        }
      : {
          url: requestConfig,
          headers: {
            authorization: token && `Bearer ${token}`,
          },
        };

  const result = useAxios<TResponse, TBody, TError>(resolvedConfig, options);

  const error = result[0].error;
  const data = error?.response?.data;
  const service = data?.service;

  if (error && data && service === serviceName) {
    const { statusCode } = data;

    if (statusCode === 401) {
      error.isAuthError = true;
      auth.signIn();
    }

    if (statusCode === 402 && data.error === "Payment Required") {
      error.isAuthError = true;
      auth.selectClient();
    }

    if (statusCode === 403) {
      error.isAuthError = true;
      auth.selectClient();
    }
  }

  return result;
};

export const makeAxiosInterceptorsInstance = (
  auth: CodatAuthContextType,
  baseURL: string
): AxiosInstance => {
  const token = auth.userData?.access_token;

  const axiosInstance = axios.create({
    withCredentials: true,
    headers: {
      "Content-Type": "application/json;charset=UTF-8",
      authorization: token ? `Bearer ${token}` : "",
    },
    baseURL,
  });

  axiosInstance.interceptors.response.use(
    (res) => res,
    (err: AxiosError<ApiError>) => {
      const statusCode = err.response?.status;
      switch (statusCode) {
        case 401: {
          auth.signIn();
          return Promise.reject(err);
        }
        case 402:
        case 403: {
          auth.selectClient();
          return Promise.reject(err);
        }
        default:
          throw err;
      }
    }
  );
  return axiosInstance;
};

export type FetchInstance = (
  endpoint: string,
  options?: RequestInit
) => Promise<Response>;
/**
 * Create a custom fetch instance that includes the auth token in the headers. Mimics makeAxiosInterceptorsInstance
 * @param auth CodatAuthContextType
 * @param baseURL string
 * @returns
 */
export const makeFetchInstance = (
  auth: CodatAuthContextType,
  baseURL: string
): FetchInstance => {
  const token = auth.userData?.access_token;

  // This is our custom fetch "instance"
  return async (
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Response> => {
    // Merge default options with any provided options
    const defaultOptions: RequestInit = {
      credentials: "include", // sends cookies with the request
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        authorization: token ? `Bearer ${token}` : "",
      },
    };

    // Merge headers correctly
    const mergedOptions: RequestInit = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...(options.headers || {}),
      },
    };

    const url = `${baseURL}${endpoint}`;

    const response = await fetch(url, mergedOptions);

    if (!response.ok) {
      switch (response.status) {
        case 401:
          auth.signIn();
          break;
        case 402:
        case 403:
          auth.selectClient();
          break;
        default:
          break;
      }

      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: response.statusText };
      }

      return Promise.reject({
        response: {
          status: response.status,
          data: errorData,
        },
      });
    }

    return response;
  };
};
